import { useCallback, useContext, useMemo } from 'react'

import { MobXProviderContext } from 'mobx-react'

import { IMutation, IQuery, IUserProject } from '~/client/graph'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import ProjectSetUpPageStore from '~/client/src/desktop/views/ProjectSetUp/ProjectSetUpPage.store'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import User from '~/client/src/shared/models/User'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import { IServiceUserDto } from '~/client/src/shared/types/UserDto'
import {
  countPhoneClaimConflicts,
  formatBulkSaveError,
} from '~/client/src/shared/utils/bulkSaveErrorFormatter'
import { toast } from '~/client/src/shared/utils/toast'
import { downloadFile } from '~/client/src/shared/utils/util'

interface ProvidedStores {
  eventsStore: DesktopEventStore
  projectMembersStore: ProjectMembersStore
  userProjectsStore: UserProjectsStore
  projectSetUpPageStore: ProjectSetUpPageStore
  state: DesktopInitialState
}

export interface UserActionsResult {
  saveMembers: (
    items: IServiceUserDto[],
    reassignClaimedPhone?: boolean,
  ) => Promise<void>
  inviteMembers: (users: User[]) => Promise<boolean>
  approveMembers: (users: User[]) => Promise<boolean>
  setMembersAsNotInvited: (users: User[]) => Promise<boolean>
  deleteMembers: (users: User[]) => Promise<void>
  updateMembersSettings: (items: IUserProject[]) => Promise<void>
  exportCsv: () => void
  importCsv: (
    file: File,
    opts?: {
      reassignClaimedPhone?: boolean
      onPhoneClaimConflicts?: (count: number) => void
    },
  ) => Promise<void>
  getUsersFromProcore: (
    onSuccess: (companies: any[]) => void,
    onError: (err: Error | string) => void,
  ) => void
  resetPassword: (user: User) => void
}

export function useUserActions(): UserActionsResult {
  const {
    eventsStore,
    projectMembersStore,
    state,
    userProjectsStore,
    projectSetUpPageStore,
  } = useContext(MobXProviderContext) as unknown as ProvidedStores

  const saveMembers = useCallback(
    async (items: IServiceUserDto[], reassignClaimedPhone: boolean = false) => {
      await projectMembersStore.saveMembers(items, reassignClaimedPhone)
    },
    [projectMembersStore],
  )

  const inviteMembers = useCallback(
    async (users: User[]) => {
      const ok = await projectMembersStore.inviteMembers(users)
      if (ok) {
        const { manyUsersWereInvitedToProject, oneUserWasInvitedToProject } =
          Localization.translator.userInviteMessages
        const msg =
          users.length > 1
            ? manyUsersWereInvitedToProject(users.length)
            : oneUserWasInvitedToProject(users[0]?.fullName || '')
        toast.success(msg)
      }
      return ok
    },
    [projectMembersStore],
  )

  const approveMembers = useCallback(
    async (users: User[]) => {
      return projectMembersStore.approveProjectMembers(users)
    },
    [projectMembersStore],
  )

  const setMembersAsNotInvited = useCallback(
    async (users: User[]) => {
      return projectMembersStore.resetInvitation(users)
    },
    [projectMembersStore],
  )

  const deleteMembers = useCallback(
    async (users: User[]) => {
      await projectMembersStore.deleteMembers(users)
    },
    [projectMembersStore],
  )

  const updateMembersSettings = useCallback(
    async (items: IUserProject[]) => {
      await userProjectsStore.save(items)
    },
    [userProjectsStore],
  )

  const exportCsv = useCallback(() => {
    eventsStore.dispatch(
      e.DOWNLOAD_PROJECT_MEMBERS,
      ({ getCsvUsers: fileContent }: IQuery) => {
        const blob = new Blob([fileContent], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const projectName = state.activeProject?.name || 'project'
        const filename = `${Localization.translator.projectMembers}-${projectName}.csv`
        downloadFile(filename, url)
        window.URL.revokeObjectURL(url)
      },
    )
  }, [eventsStore, state.activeProject])

  const importCsv = useCallback(
    async (
      file: File,
      opts?: {
        reassignClaimedPhone?: boolean
        // Called (instead of the error toast) when the upload failed only/partly
        // because some numbers are already claimed, so the UI can offer a reassign.
        onPhoneClaimConflicts?: (count: number) => void
      },
    ) => {
      const fileContent = await file.text()
      eventsStore.dispatch(
        e.UPLOAD_PROJECT_MEMBERS,
        fileContent,
        opts?.reassignClaimedPhone ?? false,
        (data: IMutation) => {
          void projectSetUpPageStore.setCurrentPageAsCompletedOnProject(
            file.name,
          )
          // Always surface the imported count, including 0, so the result
          // is unambiguous (e.g. a CSV of only blank/comma rows imports
          // nothing rather than silently "succeeding").
          const importedCount = data?.uploadUsersFromCsv?.length ?? 0
          toast.success(
            Localization.translator.bulkSaveErrors.xMembersImported(
              importedCount,
            ),
          )
        },
        (err: unknown) => {
          // Toasts collapse newlines, so join row details inline. Always shown —
          // a claimed-number conflict must not hide the OTHER row errors
          // (invalid emails, duplicates, ...) that came back with it.
          toast.error(formatBulkSaveError(err, '; '))
          // Additionally, claimed numbers get an actionable path: an admin can
          // reassign them and re-import.
          const claimed = countPhoneClaimConflicts(err)
          if (claimed > 0 && opts?.onPhoneClaimConflicts) {
            opts.onPhoneClaimConflicts(claimed)
          }
        },
      )
    },
    [eventsStore, projectSetUpPageStore],
  )

  const getUsersFromProcore = useCallback(
    (
      onSuccess: (companies: any[]) => void,
      onError: (err: Error | string) => void,
    ) => {
      eventsStore.dispatch(
        e.GET_USERS_FROM_PROCORE,
        (companies: any[]) => {
          state.loading.set(e.GET_USERS_FROM_PROCORE, false)
          onSuccess(companies)
        },
        (err: Error | string) => {
          state.loading.set(e.GET_USERS_FROM_PROCORE, false)
          onError(err)
        },
      )
    },
    [eventsStore, state.loading],
  )

  const resetPassword = useCallback(
    (user: User) => {
      if (!user?.email) return
      eventsStore.dispatch(e.REQUEST_RESET_PASSWORD, user.email)
    },
    [eventsStore],
  )

  return useMemo(
    () => ({
      saveMembers,
      inviteMembers,
      approveMembers,
      setMembersAsNotInvited,
      deleteMembers,
      updateMembersSettings,
      exportCsv,
      importCsv,
      getUsersFromProcore,
      resetPassword,
    }),
    [
      saveMembers,
      inviteMembers,
      approveMembers,
      setMembersAsNotInvited,
      deleteMembers,
      updateMembersSettings,
      exportCsv,
      importCsv,
      getUsersFromProcore,
      resetPassword,
    ],
  )
}
